import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { checkUserRole } from '@/utils/adminUtils';
import { isOnStagingUrl } from '@/utils/featureFlags';

const MIGRATION_ALLOWED_EMAIL = 'platform+b@figfinance.io';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import { OrgPickerPanel } from '@/components/admin/migration/OrgPickerPanel';
import { MigrationRunPanel, type MigrationRun, type OrgRunStatus } from '@/components/admin/migration/MigrationRunPanel';
import { AdminNav } from '@/components/admin/AdminNav';

interface Org {
  id: string;
  name: string;
}

function generateMaskSeed(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_FAILURES = 5;

const StagingMigration = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [run, setRun] = useState<MigrationRun | null>(null);
  const pollFailures = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRunId = useRef<string | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate('/login'); return; }
      const isUserAdmin = await checkUserRole(user.email ?? '', 'admin');
      if (!isUserAdmin) { navigate('/admin'); return; }
      if (user.email !== MIGRATION_ALLOWED_EMAIL) { navigate('/admin'); return; }
    };
    checkAdmin();
  }, [navigate]);

  useEffect(() => {
    supabase
      .from('organisations')
      .select('id, name')
      .order('name')
      .then(({ data, error }) => {
        if (!isMounted.current) return;
        if (error) {
          toast({ title: 'Failed to load organisations', description: error.message, variant: 'destructive' });
        }
        setOrgs(data ?? []);
        setOrgsLoading(false);
      });
  }, [toast]);

  useEffect(() => {
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, []);

  const poll = useCallback(async (runId: string) => {
    try {
      const { data, error } = await supabase
        .from('migration_runs')
        .select('status, result, created_at, completed_at, org_ids')
        .eq('id', runId)
        .single();

      if (error) throw error;

      pollFailures.current = 0;

      const rawPartial = (data.result as {
        partial?: { migrated?: string[]; skipped?: string[]; errors?: Record<string, string> };
      } | null)?.partial;

      const migrated = new Set(rawPartial?.migrated ?? []);
      const skipped = new Set(rawPartial?.skipped ?? []);
      const errors = rawPartial?.errors ?? {};
      const runOrgIds = (data.org_ids ?? []) as string[];

      const VALID_STATUSES = new Set<string>(['running', 'completed', 'failed']);
      const status: MigrationRun['status'] = VALID_STATUSES.has(data.status as string)
        ? (data.status as MigrationRun['status'])
        : 'failed';

      if (!isMounted.current) return;
      setRun(prev => {
        if (!prev || prev.runId !== runId) return prev;
        const nameMap = new Map(prev.orgStatuses.map(os => [os.orgId, os.orgName]));
        const orgStatuses: OrgRunStatus[] = runOrgIds.map(id => ({
          orgId: id,
          orgName: nameMap.get(id) ?? id,
          state: migrated.has(id) ? 'completed'
            : skipped.has(id) ? 'skipped'
              : id in errors ? 'failed'
                : 'pending',
          ...(id in errors && { error: errors[id] }),
        }));
        return { ...prev, status, completedAt: data.completed_at ?? undefined, orgStatuses };
      });

      if (status === 'running' && activeRunId.current === runId) {
        pollTimer.current = setTimeout(() => poll(runId), POLL_INTERVAL_MS);
      }
    } catch {
      pollFailures.current += 1;
      if (!isMounted.current) return;
      if (pollFailures.current >= MAX_POLL_FAILURES) {
        setRun(prev => prev?.runId === runId ? { ...prev, pollError: true } : prev);
      } else if (activeRunId.current === runId) {
        pollTimer.current = setTimeout(() => poll(runId), POLL_INTERVAL_MS);
      }
    }
  }, []);

  const handleSubmit = async (orgIds: string[], force: boolean) => {
    setIsSubmitting(true);
    if (pollTimer.current) clearTimeout(pollTimer.current);
    try {
      const maskSeed = generateMaskSeed();
      const { data: { session } } = await supabase.auth.getSession();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const apiKey = ((import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY as string | undefined)
        ?? (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string));

      const response = await fetch(`${supabaseUrl}/functions/v1/migrate-organisations-v1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token ?? apiKey}`,
          'apikey': apiKey,
        },
        body: JSON.stringify({ orgIds, options: { maskSeed, force } }),
      });

      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || `HTTP ${response.status}`);
      }

      const runId = response.headers.get('X-Migration-Run-Id');
      if (!runId) throw new Error('Migration started but no run ID was returned.');

      // Cancel the streaming NDJSON body — the run ID is in the response header
      response.body?.cancel().catch(() => undefined);

      const orgNameMap = new Map(orgs.map(o => [o.id, o.name]));
      const orgStatuses: OrgRunStatus[] = orgIds.map(id => ({
        orgId: id,
        orgName: orgNameMap.get(id) ?? id,
        state: 'pending',
      }));

      pollFailures.current = 0;
      activeRunId.current = runId;
      setRun({ runId, status: 'running', startedAt: new Date().toISOString(), orgStatuses });
      pollTimer.current = setTimeout(() => poll(runId), POLL_INTERVAL_MS);
    } catch (err) {
      toast({
        title: 'Migration failed to start',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isOnStagingUrl()) {
    navigate('/admin', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Migrate to Staging</h1>
          <AdminNav />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <OrgPickerPanel
            orgs={orgs}
            loading={orgsLoading}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
          />
          <MigrationRunPanel run={run} />
        </div>
      </main>
    </div>
  );
};

export default StagingMigration;
